export interface CimmpleRxPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted: boolean;
  cta: string;
}

export interface CimmpleRxFaq {
  q: string;
  a: string;
}

export interface CimmpleRxLaunchOffer {
  id: string;
  /** Set to false to hide this offer without removing its content. */
  enabled: boolean;
  badge: string;
  title: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted: boolean;
  cta: string;
  /** Opens the signup modal with this label as the selected plan. */
  modalPlan: string;
  modalTitle: string;
  modalDescription: string;
  modalSubmitLabel: string;
}

export interface CimmpleRxTimelineStep {
  step: string;
  title: string;
  description: string;
}

/** Single source of truth for CimmpleRx SaaS pricing plans. */
export const cimmpleRxPlans: CimmpleRxPlan[] = [
  {
    name: 'Starter',
    price: '₹799',
    period: '/month',
    description: 'or ₹7,999/year. Best for single pharmacy, 1–3 users.',
    features: [
      'Unlimited billing',
      'Inventory management',
      'GST billing & reports',
      'Batch & expiry management',
      'Purchase management',
      'Cloud backup',
      'Mobile access',
      'Basic AI search & assistance',
      'Email support',
    ],
    highlighted: false,
    cta: 'Get Started',
  },
  {
    name: 'Professional',
    price: '₹1,499',
    period: '/month',
    description: 'or ₹14,999/year.',
    features: [
      'Everything in Starter',
      'AI-powered purchase invoice OCR',
      'AI demand forecasting',
      'AI expiry prediction',
      'WhatsApp billing & communication',
      'Customer loyalty management',
      'Advanced dashboards & analytics',
      'Multi-user support',
      'API integrations',
      'Priority support',
    ],
    highlighted: true,
    cta: 'Get Started',
  },
  {
    name: 'Premium',
    price: '₹2,999',
    period: '/month',
    description: 'or ₹29,999/year.',
    features: [
      'Everything in Professional',
      'Multi-branch management',
      'Centralized inventory',
      'Advanced business analytics',
      'AI business assistant',
      'Supplier intelligence',
      'Dedicated onboarding',
    ],
    highlighted: false,
    cta: 'Get Started',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: 'Pricing',
    description: 'Designed for pharmacy chains and large organizations.',
    features: [
      'Multi-location deployment',
      'Custom integrations',
      'Dedicated account management',
      'SLA & priority support',
    ],
    highlighted: false,
    cta: 'Contact Sales',
  },
];

export const cimmpleRxStartingPrice = 'From ₹799/month';

/** Shared Founding Customer offer values — single source of truth. */
export const cimmpleRxFoundingCustomerOffer = {
  pharmacyLimit: 25,
  price: '₹699',
  period: '/month for life',
  priceForLife: '₹699/month for life',
  eligibilityLabel: 'First 25 pharmacies only',
  grandfatherNote:
    'This grandfathered price stays valid as long as your subscription remains active.',
  features: [
    'Premium onboarding at no additional cost',
    'Free migration from existing pharmacy ERP systems (including MARG and similar solutions)',
    'Dedicated onboarding assistance',
    'Priority support',
    'Early access to new AI features',
    'Opportunity to provide product feedback and influence the roadmap',
  ],
} as const;

/**
 * Limited-time launch offers for CimmpleRx.
 * Toggle `enabled` at the section or offer level to show/hide without code changes.
 */
export const cimmpleRxLaunchOffersConfig = {
  enabled: true,
  eyebrow: 'Launch Offers',
  title: 'Limited-time launch offers',
  subtitle:
    'Join early and lock in special pricing while we onboard the first pharmacies onto CimmpleRx.',
  offers: [
    {
      id: 'beta',
      enabled: true,
      badge: 'Beta Program',
      title: 'Beta Program',
      price: '6 months',
      period: 'free',
      description:
        'For selected early adopters willing to provide feedback during the beta period. Beta access is limited.',
      features: [
        '6 months free for selected early adopters',
        'Limited beta access',
        'For pharmacies willing to share product feedback',
      ],
      highlighted: false,
      cta: 'Apply for Beta',
      modalPlan: 'Beta Program',
      modalTitle: 'Apply for Beta Program',
      modalDescription:
        'Apply for limited beta access. Our team will review your pharmacy and follow up.',
      modalSubmitLabel: 'Apply for Beta',
    },
    {
      id: 'founding',
      enabled: true,
      badge: 'Founding Customer',
      title: 'Founding Customer',
      price: cimmpleRxFoundingCustomerOffer.price,
      period: cimmpleRxFoundingCustomerOffer.period,
      description: `Limited to the first ${cimmpleRxFoundingCustomerOffer.pharmacyLimit} pharmacies. ${cimmpleRxFoundingCustomerOffer.grandfatherNote}`,
      features: [
        cimmpleRxFoundingCustomerOffer.eligibilityLabel,
        `${cimmpleRxFoundingCustomerOffer.priceForLife} (grandfathered)`,
        ...cimmpleRxFoundingCustomerOffer.features,
      ],
      highlighted: true,
      cta: 'Become a Founding Customer',
      modalPlan: 'Founding Customer',
      modalTitle: 'Become a Founding Customer',
      modalDescription: `Lock in ${cimmpleRxFoundingCustomerOffer.priceForLife} with Premium Onboarding as one of the first ${cimmpleRxFoundingCustomerOffer.pharmacyLimit} pharmacies.`,
      modalSubmitLabel: 'Become a Founding Customer',
    },
  ] as CimmpleRxLaunchOffer[],
};

/** Pricing timeline explaining how launch offers progress into standard pricing. */
export const cimmpleRxPricingTimeline = {
  enabled: true,
  eyebrow: 'Pricing Timeline',
  title: 'How launch pricing works',
  subtitle:
    'Offers apply in order. Once Beta and Founding Customer allocations are exhausted, standard plan pricing applies.',
  steps: [
    {
      step: '1',
      title: 'Beta Program',
      description:
        'Selected early adopters receive 6 months free while providing product feedback.',
    },
    {
      step: '2',
      title: 'Founding Customer',
      description: `After the beta allocation is filled, the first ${cimmpleRxFoundingCustomerOffer.pharmacyLimit} pharmacies can lock in ${cimmpleRxFoundingCustomerOffer.priceForLife} with Premium Onboarding.`,
    },
    {
      step: '3',
      title: 'Standard Pricing',
      description:
        'Once both Beta and Founding Customer allocations are exhausted, the standard pricing shown in the pricing cards applies.',
    },
  ] as CimmpleRxTimelineStep[],
};

/** Onboarding, migration, and enterprise implementation messaging. */
export const cimmpleRxOnboardingConfig = {
  enabled: true,
  eyebrow: 'Onboarding & Migration',
  title: 'Get started with confidence',
  subtitle: 'Setup, training, and migration support included—so you can go live without surprise fees.',
  includedAtNoExtraCost: {
    title: 'Included at no extra cost',
    items: [
      'No setup fees',
      'Free onboarding',
      'Free cloud setup',
      'Free standard training',
      'No installation required',
      'No hidden charges',
    ],
  },
  dataMigration: {
    title: 'Data migration',
    items: [
      'Free CSV/Excel data import',
      'Free migration from existing pharmacy ERP systems (including MARG and similar solutions) for Founding Customers',
      'Advanced or enterprise-scale migrations may require a custom implementation quote',
    ],
  },
  enterpriseServices: {
    title: 'Enterprise implementation',
    description:
      'Enterprise implementation is customized for your organization. Pricing is included in your custom enterprise quote—there are no fixed setup fees listed here.',
    items: [
      'Multi-branch setup',
      'Dedicated onboarding',
      'Data migration',
      'Staff training',
      'Custom integrations',
      'Go-live assistance',
    ],
  },
};

/** CimmpleRx FAQs shown on the India pricing page. */
export const cimmpleRxFaqs: CimmpleRxFaq[] = [
  {
    q: 'What is included in Starter?',
    a: 'Starter includes unlimited billing, inventory management, GST billing and reports, batch and expiry management, purchase management, cloud backup, mobile access, basic AI search and assistance, and email support.',
  },
  {
    q: 'What does Professional add?',
    a: 'Professional adds AI-powered purchase invoice OCR, demand forecasting, expiry prediction, WhatsApp billing, loyalty management, advanced analytics, multi-user support, API integrations, and priority support.',
  },
  {
    q: 'When should I choose Premium or Enterprise?',
    a: 'Premium is ideal for multi-branch pharmacies that need centralized inventory and advanced AI tools. Enterprise offers custom pricing for pharmacy chains needing multi-location deployment, custom integrations, and dedicated account management.',
  },
  {
    q: 'What launch offers are available?',
    a: `Beta users receive 6 months free for selected early adopters. After the beta allocation is filled, the Founding Customer offer of ${cimmpleRxFoundingCustomerOffer.priceForLife} with Premium Onboarding is available to the first ${cimmpleRxFoundingCustomerOffer.pharmacyLimit} pharmacies. Once both Beta and Founding Customer allocations are exhausted, standard pricing applies.`,
  },
  {
    q: 'Is onboarding and migration included?',
    a: 'Yes. There are no setup fees, and free onboarding, cloud setup, and standard training are included. CSV/Excel import is free. Founding Customers also receive Premium Onboarding and free migration from existing pharmacy ERPs such as MARG. Advanced or enterprise-scale migrations may need a custom quote.',
  },
  {
    q: 'How does Enterprise implementation work?',
    a: 'Enterprise implementation is customized and may include multi-branch setup, dedicated onboarding, data migration, staff training, custom integrations, and go-live assistance. Implementation pricing is part of your custom enterprise quote.',
  },
  {
    q: 'Can I change plans?',
    a: 'Contact our team to discuss moving between Starter, Professional, Premium, and Enterprise.',
  },
  {
    q: 'Do you offer yearly billing?',
    a: 'Yes. Each plan lists a monthly price and an annual option—Starter at ₹7,999/year, Professional at ₹14,999/year, and Premium at ₹29,999/year.',
  },
  {
    q: 'How do I get started?',
    a: 'Choose a plan or launch offer and send your details, or contact sales for Enterprise. Our team will contact you with the next steps.',
  },
];

export const getEnabledCimmpleRxLaunchOffers = (): CimmpleRxLaunchOffer[] =>
  cimmpleRxLaunchOffersConfig.enabled
    ? cimmpleRxLaunchOffersConfig.offers.filter((offer) => offer.enabled)
    : [];

export const getCimmpleRxLaunchOfferByModalPlan = (
  modalPlan: string
): CimmpleRxLaunchOffer | undefined =>
  cimmpleRxLaunchOffersConfig.offers.find((offer) => offer.modalPlan === modalPlan);
